import { useState, useRef, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'

const NAVY = '#002E53'
const GOLD = '#C5A059'
const FONT = "'Poppins', sans-serif"

// ── UAE areas consistent with offplan bar ──────────────────────────────────
const AREAS = [
  'Dubai Marina', 'Palm Jumeirah', 'Downtown Dubai', 'Business Bay',
  'JVC (Jumeirah Village Circle)', 'Dubai Hills Estate', 'MBR City',
  'Dubai Harbour', 'Dubai Studio City', 'Jumeirah', 'Al Barsha',
  'DIFC', 'Dubai Creek Harbour', 'Emaar Beachfront', 'Arjan',
  'Deira', 'Bur Dubai', 'Al Quoz', 'Mirdif', 'Sharjah',
  'Abu Dhabi', 'Ajman', 'Ras Al Khaimah',
]

const BEDROOM_OPTIONS = [
  { label: 'Studio', value: '0' },
  { label: '1 BR',   value: '1' },
  { label: '2 BR',   value: '2' },
  { label: '3 BR',   value: '3' },
  { label: '4 BR',   value: '4' },
  { label: '5+ BR',  value: '5' },
]

// Property type categories — same slugs used in routes
export const PROPERTY_TYPES = [
  { slug: 'residential', label: 'Residential', icon: '🏠' },
  { slug: 'office',      label: 'Office',      icon: '🏢' },
  { slug: 'warehouse',   label: 'Warehouse',   icon: '🏭' },
  { slug: 'hotel',       label: 'Hotel',       icon: '🏨' },
  { slug: 'plots',       label: 'Plots',       icon: '🗺️' },
  { slug: 'buildings',   label: 'Buildings',   icon: '🏗️' },
]

import { GLOBAL_CURRENCIES, useCurrencies } from '../context/CurrencyContext'

export const LIFESTYLE_OPTIONS = [
  { id: 'waterfront', label: 'Waterfront', icon: '🌊' },
  { id: 'family',     label: 'Family',     icon: '👨‍👩‍👧‍👦' },
  { id: 'luxury',     label: 'Luxury',     icon: '💎' },
  { id: 'investment', label: 'Investment', icon: '📈' },
]

const SORT_OPTIONS = [
  { value: 'default',    label: 'Recommended' },
  { value: 'price_asc',  label: 'Price: Low → High' },
  { value: 'price_desc', label: 'Price: High → Low' },
]

// AED bounds
const PRICE_MIN_ABS = 100_000
const PRICE_MAX_ABS = 50_000_000

function toDisplay(aedVal, currency) {
  const cur = GLOBAL_CURRENCIES.find(c => c.code === currency) || GLOBAL_CURRENCIES[0]
  return aedVal * cur.rate
}

function fmtDisplay(val, currency) {
  const cur = GLOBAL_CURRENCIES.find(c => c.code === currency) || GLOBAL_CURRENCIES[0]
  if (val >= 1_000_000) return `${cur.code} ${(val / 1_000_000).toFixed(val % 1_000_000 < 50_000 ? 0 : 1)}M`
  if (val >= 1_000)     return `${cur.code} ${(val / 1_000).toFixed(0)}K`
  return `${cur.code} ${Math.round(val).toLocaleString()}`
}

// ── Portal-based pill dropdown ─────────────────────────────────────────────
function PillDrop({ id, label, icon, active, badge, children, minWidth = 200 }) {
  const [open, setOpen] = useState(false)
  const [pos, setPos]   = useState({ top: 0, left: 0 })
  const btnRef   = useRef()
  const panelRef = useRef()
  const close = useCallback(() => setOpen(false), [])

  useEffect(() => {
    if (!open) return
    
    function updatePos() {
      if (btnRef.current) {
        const rect = btnRef.current.getBoundingClientRect()
        setPos({ top: rect.bottom + 8, left: rect.left })
      }
    }
    updatePos()

    function h(e) {
      if (
        btnRef.current   && !btnRef.current.contains(e.target) &&
        panelRef.current && !panelRef.current.contains(e.target)
      ) close()
    }
    document.addEventListener('mousedown', h)
    window.addEventListener('scroll', updatePos, true)
    window.addEventListener('resize', updatePos)
    
    return () => {
      document.removeEventListener('mousedown', h)
      window.removeEventListener('scroll', updatePos, true)
      window.removeEventListener('resize', updatePos)
    }
  }, [open, close])

  function toggle() {
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect()
      setPos({ top: rect.bottom + 8, left: rect.left })
    }
    setOpen(o => !o)
  }

  return (
    <div style={{ position: 'relative', flexShrink: 0 }}>
      <button ref={btnRef} id={id} onClick={toggle}
        className={`rfb-pill${active ? ' rfb-pill--on' : ''}`}>
        {icon && <span className="rfb-pill-icon">{icon}</span>}
        <span className="rfb-pill-label">{label}</span>
        {badge != null && badge > 0 && <span className="rfb-pill-badge">{badge}</span>}
        <span className={`rfb-chevron${open ? ' up' : ''}`}>›</span>
      </button>
      {open && createPortal(
        <div ref={panelRef} className="rfb-panel"
          style={{ minWidth, position: 'fixed', top: pos.top, left: pos.left, zIndex: 9999 }}>
          {children(close)}
        </div>,
        document.body
      )}
    </div>
  )
}

// ── Currency selector ──────────────────────────────────────────────────────
function CurrencyPill({ value, onChange }) {
  const { currencies } = useCurrencies()
  const [open, setOpen] = useState(false)
  const [pos, setPos]   = useState({ top: 0, right: 0 })
  const btnRef   = useRef()
  const panelRef = useRef()
  const close = useCallback(() => setOpen(false), [])
  const sel = currencies.find(c => c.code === value) || currencies[0]

  useEffect(() => {
    if (!open) return
    
    function updatePos() {
      if (btnRef.current) {
        const rect = btnRef.current.getBoundingClientRect()
        setPos({ top: rect.bottom + 8, right: window.innerWidth - rect.right })
      }
    }
    updatePos()

    function h(e) {
      if (
        btnRef.current   && !btnRef.current.contains(e.target) &&
        panelRef.current && !panelRef.current.contains(e.target)
      ) close()
    }
    document.addEventListener('mousedown', h)
    window.addEventListener('scroll', updatePos, true)
    window.addEventListener('resize', updatePos)
    
    return () => {
      document.removeEventListener('mousedown', h)
      window.removeEventListener('scroll', updatePos, true)
      window.removeEventListener('resize', updatePos)
    }
  }, [open, close])

  function toggle() {
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect()
      setPos({ top: rect.bottom + 8, right: window.innerWidth - rect.right })
    }
    setOpen(o => !o)
  }

  return (
    <div style={{ position: 'relative', flexShrink: 0 }}>
      <button ref={btnRef} id="rfb-currency-toggle" onClick={toggle}
        className="rfb-pill rfb-pill--ghost" title="Display currency">
        <span>{sel.flag}</span>
        <span style={{ fontWeight: 700 }}>{sel.code}</span>
        <span className={`rfb-chevron${open ? ' up' : ''}`}>›</span>
      </button>
      {open && createPortal(
        <div ref={panelRef} className="rfb-panel"
          style={{ minWidth: 210, position: 'fixed', top: pos.top, right: pos.right, left: 'auto', zIndex: 9999 }}>
          <div className="rfb-panel-header">Display Currency</div>
          {currencies.map(c => (
            <button key={c.code} id={`rfb-currency-${c.code.toLowerCase()}`}
              className={`rfb-panel-item${value === c.code ? ' on' : ''}`}
              onClick={() => { onChange(c.code); setOpen(false) }}>
              <span style={{ fontSize: 17 }}>{c.flag}</span>
              <span style={{ fontWeight: 700, minWidth: 36 }}>{c.code}</span>
              <span style={{ color: '#94a3b8', fontSize: 12 }}>{c.name}</span>
              {value === c.code && <span style={{ marginLeft: 'auto', color: GOLD, fontSize: 13, fontWeight: 700 }}>✓</span>}
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  )
}

// ── Dual budget slider ─────────────────────────────────────────────────────
function BudgetSlider({ minAed, maxAed, currency, onChange }) {
  const minDisp  = toDisplay(minAed,        currency)
  const maxDisp  = toDisplay(maxAed,        currency)
  const minLimit = toDisplay(PRICE_MIN_ABS, currency)
  const maxLimit = toDisplay(PRICE_MAX_ABS, currency)
  const step     = toDisplay(100_000,       currency)

  const minFrac = (minAed - PRICE_MIN_ABS) / (PRICE_MAX_ABS - PRICE_MIN_ABS)
  const maxFrac = (maxAed - PRICE_MIN_ABS) / (PRICE_MAX_ABS - PRICE_MIN_ABS)

  const { currencies } = useCurrencies()
  const cur  = currencies.find(c => c.code === currency) || currencies[0]
  const toAed = v => Math.round(v / cur.rate)

  return (
    <div style={{ padding: '4px 0' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16, gap: 8 }}>
        <div style={{ textAlign: 'center' }}>
          <div className="rfb-slider-label">MIN</div>
          <div className="rfb-slider-val">{fmtDisplay(minDisp, currency)}</div>
        </div>
        <div style={{ alignSelf: 'center', color: '#cbd5e1' }}>—</div>
        <div style={{ textAlign: 'center' }}>
          <div className="rfb-slider-label">MAX</div>
          <div className="rfb-slider-val">
            {maxAed >= PRICE_MAX_ABS ? 'No limit' : fmtDisplay(maxDisp, currency)}
          </div>
        </div>
      </div>
      <div style={{ position: 'relative', height: 28, display: 'flex', alignItems: 'center' }}>
        <div style={{ position: 'absolute', left: 0, right: 0, height: 5, background: '#e2e8f0', borderRadius: 4 }} />
        <div style={{
          position: 'absolute', height: 5, borderRadius: 4,
          background: `linear-gradient(90deg, ${GOLD}, #e8c97a)`,
          left: `${minFrac * 100}%`, right: `${(1 - maxFrac) * 100}%`,
        }} />
        <input type="range" id="rfb-slider-min"
          min={minLimit} max={maxLimit} step={step} value={minDisp}
          onChange={e => { const aed = toAed(Number(e.target.value)); if (aed < maxAed) onChange(aed, maxAed) }}
          style={{ position: 'absolute', width: '100%', opacity: 0, cursor: 'pointer', height: 28, zIndex: 2 }} />
        <input type="range" id="rfb-slider-max"
          min={minLimit} max={maxLimit} step={step} value={maxDisp}
          onChange={e => { const aed = toAed(Number(e.target.value)); if (aed > minAed) onChange(minAed, aed) }}
          style={{ position: 'absolute', width: '100%', opacity: 0, cursor: 'pointer', height: 28, zIndex: 3 }} />
        {[minFrac, maxFrac].map((f, i) => (
          <div key={i} style={{
            position: 'absolute', left: `${f * 100}%`,
            width: 18, height: 18, borderRadius: '50%',
            background: `linear-gradient(135deg, ${GOLD}, #e8c97a)`,
            border: '2.5px solid #fff', boxShadow: '0 3px 8px rgba(197,160,89,0.5)',
            transform: 'translateX(-50%)', zIndex: 1, pointerEvents: 'none',
          }} />
        ))}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
        <span className="rfb-slider-edge">{fmtDisplay(minLimit, currency)}</span>
        <span className="rfb-slider-edge">{fmtDisplay(maxLimit, currency)}+</span>
      </div>
    </div>
  )
}

// ── Checkbox list ──────────────────────────────────────────────────────────
function CheckList({ options, selected, onChange, searchable = false }) {
  const [q, setQ] = useState('')
  const list = searchable ? options.filter(o => o.toLowerCase().includes(q.toLowerCase())) : options
  const toggle = val => onChange(selected.includes(val) ? selected.filter(x => x !== val) : [...selected, val])
  return (
    <>
      {searchable && options.length > 5 && (
        <div style={{ padding: '8px 14px 4px' }}>
          <input className="rfb-search-input" type="text" placeholder="Search…"
            value={q} onChange={e => setQ(e.target.value)} onClick={e => e.stopPropagation()} />
        </div>
      )}
      <div style={{ maxHeight: 220, overflowY: 'auto' }}>
        {list.map(opt => (
          <button key={opt} className={`rfb-panel-item${selected.includes(opt) ? ' on' : ''}`}
            onClick={() => toggle(opt)}>
            <span className={`rfb-chk${selected.includes(opt) ? ' checked' : ''}`}>
              {selected.includes(opt) ? '✓' : ''}
            </span>
            <span style={{ fontSize: 13 }}>{opt}</span>
          </button>
        ))}
        {list.length === 0 && (
          <div style={{ padding: '12px 16px', color: '#94a3b8', fontSize: 13, fontFamily: FONT }}>No results</div>
        )}
      </div>
    </>
  )
}

// ── Main Filter Bar export ─────────────────────────────────────────────────
export default function ResaleFilterBar({
  filters, onFilterChange, areaList = [],
  resultCount, totalCount, loading,
}) {
  const [expanded, setExpanded] = useState(true)
  const {
    searchQuery, minPrice, maxPrice,
    bedrooms, areas, lifestyles,
    propertyTypes, currency, sortBy,
  } = filters

  const set = (key, val) => onFilterChange({ ...filters, [key]: val })

  const hasActive =
    searchQuery ||
    minPrice > PRICE_MIN_ABS || maxPrice < PRICE_MAX_ABS ||
    bedrooms.length > 0 || areas.length > 0 ||
    propertyTypes.length > 0

  const { currencies } = useCurrencies()
  const budgetActive = minPrice > PRICE_MIN_ABS || maxPrice < PRICE_MAX_ABS
  const cur = currencies.find(c => c.code === currency) || currencies[0]

  const budgetLabel = budgetActive
    ? `${fmtDisplay(toDisplay(minPrice, currency), currency)} – ${maxPrice >= PRICE_MAX_ABS ? 'No limit' : fmtDisplay(toDisplay(maxPrice, currency), currency)}`
    : 'Budget'

  // Property type label
  const typeLabel = propertyTypes.length === 0
    ? 'Property Type'
    : propertyTypes.length === 1
      ? (PROPERTY_TYPES.find(t => t.slug === propertyTypes[0])?.label || propertyTypes[0])
      : `${propertyTypes.length} types`

  // Active filter chips
  const chips = []
  if (searchQuery) chips.push({ label: `"${searchQuery}"`, clear: () => set('searchQuery', '') })
  if (budgetActive) chips.push({
    label: `${cur.flag} ${budgetLabel}`,
    clear: () => onFilterChange({ ...filters, minPrice: PRICE_MIN_ABS, maxPrice: PRICE_MAX_ABS }),
  })
  bedrooms.forEach(b => chips.push({ label: b === '0' ? 'Studio' : `${b} BR`, clear: () => set('bedrooms', bedrooms.filter(x => x !== b)) }))
  areas.forEach(a => chips.push({ label: a, clear: () => set('areas', areas.filter(x => x !== a)) }))

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap');

        .rfb-bar {
          position: sticky; top: 110px; z-index: 1000;
          background: rgba(255,255,255,0.98);
          backdrop-filter: blur(20px); -webkit-backdrop-filter: blur(20px);
          border-bottom: 1px solid rgba(0,46,83,0.07);
          box-shadow: 0 4px 24px rgba(0,46,83,0.07);
          isolation: isolate;
        }
        /* Collapsible body */
        .rfb-collapsible {
          overflow: hidden;
          transition: max-height 0.32s cubic-bezier(0.4,0,0.2,1), opacity 0.25s ease;
        }
        .rfb-collapsible.open  { max-height: 300px; opacity: 1; }
        .rfb-collapsible.shut  { max-height: 0;     opacity: 0; pointer-events: none; }
        /* Toggle tab */
        .rfb-toggle-tab {
          display: flex; align-items: center; justify-content: center; gap: 5px;
          width: 100%; padding: 4px 0 5px;
          background: none; border: none; cursor: pointer;
          font-family: ${FONT}; font-size: 11px; font-weight: 600;
          color: #94a3b8; letter-spacing: 0.4px;
          border-top: 1px solid rgba(0,46,83,0.05);
          transition: color 0.18s, background 0.18s;
        }
        .rfb-toggle-tab:hover { color: ${NAVY}; background: rgba(0,46,83,0.03); }
        .rfb-toggle-arrow {
          display: inline-flex; transition: transform 0.28s ease;
          font-size: 13px; line-height: 1;
        }
        .rfb-toggle-arrow.down { transform: rotate(180deg); }
        .rfb-inner { max-width: 1200px; margin: 0 auto; padding: 14px 20px 10px; box-sizing: border-box; }
        .rfb-row {
          display: flex; align-items: center; gap: 8px; flex-wrap: nowrap; overflow-x: auto;
          scrollbar-width: none;
        }
        .rfb-row::-webkit-scrollbar { display: none; }

        /* Search box */
        .rfb-search {
          display: flex; align-items: center; gap: 8px;
          background: #f8fafc; border: 1.5px solid rgba(0,46,83,0.09);
          border-radius: 10px; padding: 8px 13px;
          transition: all 0.2s; flex-shrink: 0;
        }
        .rfb-search:focus-within { border-color: ${GOLD}; background: #fff; box-shadow: 0 0 0 3px rgba(197,160,89,0.1); }
        .rfb-search input { border: none; outline: none; background: transparent; font-family: ${FONT}; font-size: 13px; color: #1e293b; width: 160px; }
        .rfb-search input::placeholder { color: #94a3b8; }

        /* Pills */
        .rfb-pill {
          display: flex; align-items: center; gap: 6px;
          background: #f8fafc; border: 1.5px solid rgba(0,46,83,0.09);
          border-radius: 10px; padding: 8px 13px;
          font-family: ${FONT}; font-size: 13px; font-weight: 600;
          color: #334155; cursor: pointer; white-space: nowrap; flex-shrink: 0;
          transition: all 0.18s ease;
        }
        .rfb-pill:hover { background: #f1f5f9; border-color: rgba(0,46,83,0.18); transform: translateY(-1px); box-shadow: 0 3px 10px rgba(0,46,83,0.07); }
        .rfb-pill--on { background: rgba(197,160,89,0.10); border-color: ${GOLD}; color: #7c5a12; }
        .rfb-pill--on:hover { background: rgba(197,160,89,0.16); }
        .rfb-pill--ghost { background: transparent; border-color: transparent; color: #64748b; padding: 8px 10px; }
        .rfb-pill--ghost:hover { background: #f1f5f9; border-color: rgba(0,46,83,0.09); transform: none; box-shadow: none; }
        .rfb-pill-icon { font-size: 14px; }
        .rfb-pill-label { font-size: 13px; }
        .rfb-pill-badge { background: ${GOLD}; color: #fff; font-size: 10px; font-weight: 700; padding: 1px 6px; border-radius: 12px; min-width: 18px; text-align: center; }
        .rfb-chevron { font-size: 13px; color: #94a3b8; line-height: 1; transform: rotate(90deg); transition: transform 0.18s; }
        .rfb-chevron.up { transform: rotate(-90deg); }

        /* Divider */
        .rfb-div { width: 1px; height: 24px; background: rgba(0,46,83,0.08); flex-shrink: 0; }

        /* Panel */
        .rfb-panel {
          background: #fff; border-radius: 14px;
          border: 1px solid rgba(0,46,83,0.08);
          box-shadow: 0 20px 50px rgba(0,46,83,0.13), 0 4px 12px rgba(0,0,0,0.05);
          overflow: hidden; animation: rfb-in 0.16s ease;
        }
        @keyframes rfb-in { from { opacity: 0; transform: translateY(-6px) scale(0.98); } to { opacity: 1; transform: translateY(0) scale(1); } }
        .rfb-panel-header {
          display: flex; align-items: center; justify-content: space-between;
          padding: 10px 16px 8px;
          font-family: ${FONT}; font-size: 10px; font-weight: 700;
          color: #94a3b8; letter-spacing: 1.2px; text-transform: uppercase;
          border-bottom: 1px solid rgba(0,46,83,0.05);
        }
        .rfb-panel-clear { background: none; border: none; cursor: pointer; font-family: ${FONT}; font-size: 11px; font-weight: 600; color: #ef4444; padding: 2px 6px; border-radius: 4px; transition: background 0.15s; }
        .rfb-panel-clear:hover { background: #fef2f2; }
        .rfb-panel-item { display: flex; align-items: center; gap: 10px; width: 100%; padding: 9px 16px; background: transparent; border: none; font-family: ${FONT}; font-size: 13px; font-weight: 500; color: #374151; cursor: pointer; text-align: left; transition: background 0.12s; }
        .rfb-panel-item:hover { background: #f8fafc; }
        .rfb-panel-item.on { background: rgba(197,160,89,0.07); color: #7c5a12; }
        .rfb-panel-item.on:hover { background: rgba(197,160,89,0.13); }

        /* Checkbox */
        .rfb-chk { width: 16px; height: 16px; border-radius: 4px; border: 1.5px solid rgba(0,46,83,0.18); display: flex; align-items: center; justify-content: center; font-size: 10px; font-weight: 800; flex-shrink: 0; transition: all 0.12s; }
        .rfb-chk.checked { background: ${GOLD}; border-color: ${GOLD}; color: #fff; }

        /* Search inside panel */
        .rfb-search-input { width: 100%; box-sizing: border-box; border: 1.5px solid rgba(0,46,83,0.1); border-radius: 8px; padding: 7px 10px; font-family: ${FONT}; font-size: 12px; color: #1e293b; outline: none; background: #f8fafc; transition: border-color 0.15s; }
        .rfb-search-input:focus { border-color: ${GOLD}; }

        /* Budget panel */
        .rfb-budget-panel { padding: 16px 18px; min-width: 280px; }
        .rfb-slider-label { font-family: ${FONT}; font-size: 9px; font-weight: 700; color: #94a3b8; letter-spacing: 1px; text-transform: uppercase; margin-bottom: 3px; }
        .rfb-slider-val { font-family: ${FONT}; font-size: 15px; font-weight: 700; color: ${GOLD}; }
        .rfb-slider-edge { font-family: ${FONT}; font-size: 11px; color: #b0b8c8; }

        /* Sort */
        .rfb-sort {
          border: 1.5px solid rgba(0,46,83,0.09); border-radius: 10px;
          padding: 8px 10px; font-family: ${FONT}; font-size: 13px; font-weight: 600;
          color: #334155; background: #f8fafc; cursor: pointer; outline: none;
          transition: all 0.18s; appearance: none; flex-shrink: 0;
          padding-right: 28px; min-width: 140px;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%2394a3b8'/%3E%3C/svg%3E");
          background-repeat: no-repeat; background-position: right 10px center; background-color: #f8fafc;
        }
        .rfb-sort:hover { border-color: rgba(0,46,83,0.18); }

        /* Clear all */
        .rfb-clear { border: 1.5px solid rgba(239,68,68,0.25); border-radius: 10px; padding: 7px 12px; font-family: ${FONT}; font-size: 12px; font-weight: 700; color: #dc2626; background: #fef2f2; cursor: pointer; transition: all 0.18s; white-space: nowrap; flex-shrink: 0; }
        .rfb-clear:hover { background: #fee2e2; }

        /* Lifestyle row */
        .rfb-life-row { display: flex; gap: 6px; flex-wrap: wrap; margin-top: 8px; align-items: center; padding-top: 8px; border-top: 1px solid rgba(0,46,83,0.05); }
        .rfb-life-label { font-family: ${FONT}; font-size: 9px; font-weight: 700; color: #b0b8c8; letter-spacing: 1.2px; text-transform: uppercase; white-space: nowrap; }
        .rfb-life-pill { display: flex; align-items: center; gap: 5px; border: 1.5px solid rgba(0,46,83,0.09); border-radius: 50px; padding: 5px 13px; font-family: ${FONT}; font-size: 12px; font-weight: 600; cursor: pointer; transition: all 0.18s; background: #f8fafc; color: #334155; white-space: nowrap; }
        .rfb-life-pill:hover { transform: translateY(-1px); box-shadow: 0 3px 8px rgba(0,46,83,0.07); }
        .rfb-life-pill.on { background: rgba(0,46,83,0.06); border-color: ${NAVY}; color: ${NAVY}; }

        /* Chips */
        .rfb-chips { display: flex; flex-wrap: wrap; gap: 6px; padding: 8px 20px; max-width: 1200px; margin: 0 auto; box-sizing: border-box; }
        .rfb-chip { display: flex; align-items: center; gap: 5px; background: rgba(197,160,89,0.10); border: 1px solid rgba(197,160,89,0.30); border-radius: 50px; padding: 4px 11px; font-family: ${FONT}; font-size: 12px; font-weight: 600; color: #7c5a12; animation: rfb-chip-in 0.14s ease; }
        @keyframes rfb-chip-in { from { opacity: 0; transform: scale(0.88); } to { opacity: 1; transform: scale(1); } }
        .rfb-chip-x { background: none; border: none; cursor: pointer; font-size: 14px; color: #92681f; padding: 0; line-height: 1; opacity: 0.6; transition: opacity 0.12s; }
        .rfb-chip-x:hover { opacity: 1; }

        /* Result count */
        .rfb-count { font-family: ${FONT}; font-size: 13px; color: #64748b; padding: 10px 20px 6px; max-width: 1200px; margin: 0 auto; box-sizing: border-box; }
        .rfb-count strong { color: ${NAVY}; }

        @media (max-width: 1100px) { .rfb-bar { top: 80px; } }
        @media (max-width: 768px) {
          .rfb-bar { top: 70px; }
          .rfb-inner { padding: 8px 12px 6px; }
          .rfb-row { gap: 5px; }
          .rfb-search input { width: 90px; }
          .rfb-search { padding: 7px 10px; }
          .rfb-pill { padding: 7px 10px; font-size: 12px; }
          .rfb-pill-label { font-size: 12px; }
          .rfb-sort { min-width: 110px; font-size: 12px; padding: 7px 24px 7px 8px; }
          .rfb-life-label { display: none; }
          .rfb-life-row { gap: 4px; padding-top: 6px; margin-top: 4px; }
          .rfb-life-pill { padding: 4px 10px; font-size: 11px; }
        }
        @media (max-width: 480px) {
          .rfb-search input { width: 70px; }
          .rfb-div { display: none; }
        }
      `}</style>

      <div className="rfb-bar">
        <div className={`rfb-collapsible ${expanded ? 'open' : 'shut'}`}>
        <div className="rfb-inner">
          {/* ── Main filter row ── */}
          <div className="rfb-row">

            {/* Search */}
            <div className="rfb-search">
              <svg width="14" height="14" viewBox="0 0 20 20" fill="none" style={{ flexShrink: 0, opacity: 0.45 }}>
                <circle cx="8.5" cy="8.5" r="5.75" stroke="#334155" strokeWidth="1.8" />
                <path d="M13 13l3.5 3.5" stroke="#334155" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
              <input id="rfb-search"
                type="text" placeholder="Property, area…"
                value={searchQuery}
                onChange={e => set('searchQuery', e.target.value)}
              />
              {searchQuery && (
                <button onClick={() => set('searchQuery', '')}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 15, padding: 0, lineHeight: 1, flexShrink: 0 }}>×</button>
              )}
            </div>

            <div className="rfb-div" />

            {/* Budget */}
            <PillDrop id="rfb-budget" label={budgetLabel} icon="💰" active={budgetActive} minWidth={280}>
              {(close) => (
                <div className="rfb-budget-panel">
                  <div className="rfb-panel-header">
                    Budget Range
                    {budgetActive && (
                      <button className="rfb-panel-clear"
                        onClick={() => onFilterChange({ ...filters, minPrice: PRICE_MIN_ABS, maxPrice: PRICE_MAX_ABS })}>
                        Reset
                      </button>
                    )}
                  </div>
                  <div style={{ padding: '12px 0 4px' }}>
                    <BudgetSlider minAed={minPrice} maxAed={maxPrice} currency={currency}
                      onChange={(mn, mx) => onFilterChange({ ...filters, minPrice: mn, maxPrice: mx })} />
                  </div>
                  <button onClick={close} style={{
                    marginTop: 14, width: '100%',
                    background: `linear-gradient(135deg, ${GOLD}, #e8c97a)`,
                    border: 'none', borderRadius: 9, padding: 10,
                    fontFamily: FONT, fontSize: 13, fontWeight: 700,
                    color: '#fff', cursor: 'pointer',
                    boxShadow: '0 3px 10px rgba(197,160,89,0.32)',
                  }}>Apply</button>
                </div>
              )}
            </PillDrop>

            {/* Bedrooms */}
            <PillDrop
              id="rfb-bedrooms"
              label={bedrooms.length === 0 ? 'Bedrooms' : bedrooms.length === 1
                ? (BEDROOM_OPTIONS.find(o => o.value === bedrooms[0])?.label || bedrooms[0])
                : `${bedrooms.length} types`}
              icon="🛏"
              active={bedrooms.length > 0}
              badge={bedrooms.length || null}
              minWidth={180}
            >
              {() => (
                <>
                  <div className="rfb-panel-header">
                    Bedrooms
                    {bedrooms.length > 0 && (
                      <button className="rfb-panel-clear" onClick={() => set('bedrooms', [])}>Clear</button>
                    )}
                  </div>
                  {BEDROOM_OPTIONS.map(opt => (
                    <button key={opt.value} id={`rfb-bed-${opt.value}`}
                      className={`rfb-panel-item${bedrooms.includes(opt.value) ? ' on' : ''}`}
                      onClick={() => {
                        const next = bedrooms.includes(opt.value)
                          ? bedrooms.filter(x => x !== opt.value)
                          : [...bedrooms, opt.value]
                        set('bedrooms', next)
                      }}>
                      <span className={`rfb-chk${bedrooms.includes(opt.value) ? ' checked' : ''}`}>
                        {bedrooms.includes(opt.value) ? '✓' : ''}
                      </span>
                      {opt.label}
                    </button>
                  ))}
                </>
              )}
            </PillDrop>

{/* Area */}
            <PillDrop
              id="rfb-area"
              label={areas.length === 0 ? 'Area' : areas.length === 1 ? areas[0] : `${areas.length} areas`}
              icon="📍"
              active={areas.length > 0}
              badge={areas.length || null}
              minWidth={230}
            >
              {() => {
                const combinedAreas = [...new Set([...AREAS, ...areaList])].sort()
                const areaOpts = combinedAreas.length > 0 ? combinedAreas : AREAS
                return (
                  <>
                    <div className="rfb-panel-header">
                      Area
                      {areas.length > 0 && (
                        <button className="rfb-panel-clear" onClick={() => set('areas', [])}>Clear</button>
                      )}
                    </div>
                    <CheckList options={areaOpts} selected={areas} onChange={v => set('areas', v)} searchable />
                  </>
                )
              }}
            </PillDrop>

            <div className="rfb-div" />

            {/* Currency */}
            <CurrencyPill value={currency} onChange={v => set('currency', v)} />

            <div className="rfb-div" />

            {/* Sort */}
            <select id="rfb-sort" className="rfb-sort" value={sortBy} onChange={e => set('sortBy', e.target.value)}>
              {SORT_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>

            {/* Clear all */}
            {hasActive && (
              <button id="rfb-clear-all" className="rfb-clear"
                onClick={() => onFilterChange({
                  searchQuery: '', minPrice: PRICE_MIN_ABS, maxPrice: PRICE_MAX_ABS,
                  bedrooms: [], areas: [], lifestyles: [], propertyTypes: [],
                  currency: 'AED', sortBy: 'default',
                })}>
                ✕ Clear
              </button>
            )}
          </div>

          {/* ── Property Type quick-click row (replaces Lifestyle) ── */}
          <div className="rfb-life-row">
            <span className="rfb-life-label">Type:</span>
            {PROPERTY_TYPES.map(opt => (
              <button
                key={opt.slug}
                id={`rfb-type-row-${opt.slug}`}
                className={`rfb-life-pill${propertyTypes.includes(opt.slug) ? ' on' : ''}`}
                onClick={() => {
                  const next = propertyTypes.includes(opt.slug)
                    ? propertyTypes.filter(x => x !== opt.slug)
                    : [...propertyTypes, opt.slug]
                  set('propertyTypes', next)
                }}
              >
                {opt.icon} {opt.label}
              </button>
            ))}
            {propertyTypes.length > 0 && (
              <button
                onClick={() => set('propertyTypes', [])}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  fontFamily: FONT, fontSize: '11px', fontWeight: 700,
                  color: '#94a3b8', padding: '5px 6px', borderRadius: '6px',
                  transition: 'color 0.15s',
                }}
                onMouseEnter={e => e.currentTarget.style.color = '#ef4444'}
                onMouseLeave={e => e.currentTarget.style.color = '#94a3b8'}
              >
                Clear
              </button>
            )}
          </div>

          {/* ── Active filter chips ── */}
          {chips.length > 0 && (
            <div className="rfb-chips">
              {chips.map((chip, i) => (
                <div key={i} className="rfb-chip">
                  {chip.label}
                  <button className="rfb-chip-x" onClick={chip.clear}>×</button>
                </div>
              ))}
            </div>
          )}
        </div>
        </div>{/* end collapsible */}
        {/* ── Collapse tab ── */}
        <button
          id="rfb-collapse-toggle"
          className="rfb-toggle-tab"
          onClick={() => setExpanded(e => !e)}
          aria-label={expanded ? 'Hide filters' : 'Show filters'}
        >
          <span className={`rfb-toggle-arrow${expanded ? '' : ' down'}`}>⌃</span>
          <span>{expanded ? 'Hide filters' : 'Show filters'}</span>
        </button>
      </div>

      {/* Result count below sticky bar */}
      {!loading && (
        <div className="rfb-count">
          {resultCount === totalCount
            ? <><strong>{totalCount}</strong> {totalCount === 1 ? 'listing' : 'listings'} available</>
            : <><strong>{resultCount}</strong> of <strong>{totalCount}</strong> listings match your filters</>}
          {hasActive && ' · filtered'}
        </div>
      )}
    </>
  )
}

// Export constants for use in PropertiesPage
export { PRICE_MIN_ABS, PRICE_MAX_ABS }
